import { ChatMessageItem } from "./chat-message-item";
import { Greeting } from "./greeting";
import { useEffect, useState } from "react";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { ChatMessage } from "@tracify/shared";
import { isTextUIPart, isToolUIPart } from "ai";
import { sanitizeText } from "@/lib/ai/utils";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { useStickToBottomContext } from "use-stick-to-bottom";
import { AssistantLoader } from "./ui/assistant-loader";
import type { ToolCallApprovalActionInput } from "./tool-call-approval";

function ScrollToBottomOnChatChange({ chatId }: { chatId: string }) {
  const { scrollToBottom } = useStickToBottomContext();

  useEffect(() => {
    if (!chatId) return;
    scrollToBottom("instant");
  }, [chatId, scrollToBottom]);

  return null;
}

interface ChatMessageListProps {
  chatId: string;
  status: UseChatHelpers<ChatMessage>["status"];
  messages: ChatMessage[];
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
  pendingToolApprovalId?: string | null;
  onApproveToolCall?: (input: ToolCallApprovalActionInput) => void;
  onDenyToolCall?: (input: ToolCallApprovalActionInput) => void;
}

// NOTE: useChat can update streamed message parts in place.
// Keep this list non-memoized so token-by-token updates always render.
export function ChatMessageList({
  chatId,
  status,
  messages,
  setMessages,
  regenerate,
  pendingToolApprovalId,
  onApproveToolCall,
  onDenyToolCall,
}: ChatMessageListProps) {
  const [hasSentMessage, setHasSentMessage] = useState(false);

  useEffect(() => {
    if (chatId) {
      setHasSentMessage(false);
    }
  }, [chatId]);

  useEffect(() => {
    if (status === "submitted") {
      setHasSentMessage(true);
    }
  }, [status]);

  const lastMessage = messages.at(-1);
  const hasRenderableContent = (message: ChatMessage) =>
    Boolean(
      message.parts?.some((part) => {
        if (isTextUIPart(part)) {
          return sanitizeText(part.text ?? "").trim().length > 0;
        }

        if (isToolUIPart(part)) {
          return true;
        }

        return false;
      })
    );

  const showSubmittedLoader =
    status === "submitted" && lastMessage?.role === "user";

  const [wasShowingSubmittedLoader, setWasShowingSubmittedLoader] =
    useState(false);
  const suppressNextAssistantAnimation =
    wasShowingSubmittedLoader && !showSubmittedLoader;

  useEffect(() => {
    setWasShowingSubmittedLoader(showSubmittedLoader);
  }, [showSubmittedLoader]);

  return (
    <Conversation>
      <ScrollToBottomOnChatChange chatId={chatId} />
      <ConversationContent>
        {messages.length === 0 && <Greeting />}

        {messages.map((message, index) => (
          <ChatMessageItem
            key={message.id}
            chatId={chatId}
            message={message}
            isLoading={status === "streaming" && messages.length - 1 === index}
            setMessages={setMessages}
            regenerate={regenerate}
            pendingToolApprovalId={pendingToolApprovalId}
            onApproveToolCall={onApproveToolCall}
            onDenyToolCall={onDenyToolCall}
            requiresScrollPadding={
              hasSentMessage &&
              index === messages.length - 1 &&
              message.role === "assistant" &&
              hasRenderableContent(message)
            }
            disableInitialAnimation={
              suppressNextAssistantAnimation &&
              index === messages.length - 1 &&
              message.role === "assistant"
            }
          />
        ))}

        {showSubmittedLoader && <AssistantLoader />}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
}
